'use client'

import { Box } from '@chakra-ui/react'
import { Dialog } from '@saas-ui/react'

import {
  useCreateDialogActions,
  useCreateDialogState,
} from '#features/common/components/create-dialog-store.ts'
import { PromptComposer } from '#features/common/components/prompt-composer.tsx'

/**
 * The global "create" dialog, opened from the sidebar (`C`) and the list pages.
 *
 * It is deliberately nothing more than a modal frame around `PromptComposer` —
 * the exact composer the home screen renders. Previously this dialog carried its
 * own tab rail and duplicate session/blueprint forms, and the sessions list page
 * had a third, prompt-less copy of its own; all of them drifted away from the
 * home screen in both layout and features. Sharing the component removes the
 * possibility of that drift instead of re-syncing the copies by hand.
 *
 * The composer lives *inside* `Dialog.Content`, which Chakra unmounts while the
 * dialog is closed (`lazyMount`/`unmountOnExit` are its defaults). So the
 * composer's state resets on close and its Zero/tRPC queries only run while it is
 * visible — for free. The previous version held that state and those queries in
 * this component's own body, outside the content boundary, which is why it needed
 * a reset-on-close effect and an `enabled: open` flag on every query.
 *
 * That unmount boundary is also why `mode` is handed to the composer as an
 * initial value rather than a controlled prop: each open mounts a fresh composer
 * on the tab the opener asked for, and the user is free to switch tabs after.
 */
export function CreateDialog() {
  const { open, mode } = useCreateDialogState()
  const actions = useCreateDialogActions()

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) actions.close()
      }}
      placement="center"
    >
      {/* The composer supplies its own surface, so the dialog contributes only
          the overlay, the elevation and the sizing of the home screen column. */}
      <Dialog.Content
        maxW="3xl"
        bg="transparent"
        boxShadow="none"
        borderWidth="0"
        px={{ base: '4', md: '6', lg: '8' }}
        py="0"
        overflow="visible"
      >
        <Dialog.Title srOnly>Start a task or session</Dialog.Title>
        <Box position="relative" borderRadius="panel.lg" boxShadow="lg">
          <PromptComposer
            defaultMode={mode}
            onCreated={actions.close}
            navigateToTask={false}
          />
          {/* The home screen has nothing to dismiss, so the composer carries no
              close control; a modal needs one. It sits in the empty right-hand
              side of the composer's tab row, above the composer's own z-index. */}
          <Dialog.CloseButton
            position="absolute"
            top="1.5"
            insetEnd="1.5"
            zIndex="2"
          />
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  )
}
